/* eslint-disable @typescript-eslint/naming-convention */

import { NextRequest, NextFetchEvent, NextResponse } from 'next/server';

import { formatInTimeZone } from 'date-fns-tz';
import { v4 as uuidV4 } from 'uuid';
import { generateCookieDomain } from '../utils/cookies/generateCookieDomain';
import { generateCookieExpire } from '../utils/cookies/generateCookieExpire';
import { getUserIdFromAccessToken } from '../utils/member/getUserIdFromAccessToken';
import { Snowflake } from '@sapphire/snowflake';

import { UTM_COOKIE_KEYS } from './constants';

import type { MiddlewareFactory } from './type';

const LOG_COOKIE_KEYS = {
  log_session_timestamp: 'log_session_timestamp',
  log_session_id: 'log_session_id',
  log_pid: 'log_pid',
  log_data: 'log_data',
};

const getRequestIp = (req: NextRequest) => {
  const fallbackIpForLocal = '0.0.0.0';
  const requestIp = req.ip ?? req.headers.get('x-real-ip');
  const forwardedForIp = req.headers.get('x-forwarded-for')?.split(',')[0] ?? null;

  try {
    const stringfiedLogData = req.cookies.get(LOG_COOKIE_KEYS.log_data)?.value ?? '{}';
    const logDataIp = JSON.parse(stringfiedLogData)?.client_ip;

    return requestIp ?? forwardedForIp ?? logDataIp ?? fallbackIpForLocal;
  } catch (error) {
    return fallbackIpForLocal;
  }
};

const epoch = new Date('2015-01-01T00:00:00.000Z');
const snowflake = new Snowflake(epoch);
// Snowflake ID에서 워커 ID를 랜덤하게 변경하는 함수
const generateLogPid = () => {
  // 워커 ID를 768~1023 사이에서 랜덤하게 생성(디바이스에 따라 분리정책 web :  768~1023 )
  const machineId = BigInt(Math.floor(Math.random() * (1023 - 768 + 1)) + 768);
  const originalId = snowflake.generate({
    // machineId를 분리해서 workerId, processId 설정
    workerId: machineId >> BigInt(5),
    processId: machineId & BigInt(0b11111),
  });

  return originalId.toString();
};
const getAllUTM = (req: NextRequest, response: NextResponse) => {
  const utmEntries = Object.values(UTM_COOKIE_KEYS).map((key) => [
    key,
    response.cookies.get(key)?.value ?? req.cookies.get(key)?.value ?? null,
  ]);

  return Object.fromEntries(utmEntries) as Record<string, string | null>;
};

export const weblogMiddleware: MiddlewareFactory = (next, response) => {
  return async (req: NextRequest, event: NextFetchEvent) => {
    const sessionTimestamp = formatInTimeZone(new Date(), 'GMT', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
    response.cookies.set(LOG_COOKIE_KEYS.log_session_timestamp, sessionTimestamp, {
      expires: generateCookieExpire(30, 'minutes'),
      domain: generateCookieDomain(),
    });

    const sessionId =
      req.cookies.get(LOG_COOKIE_KEYS.log_session_id)?.value ?? uuidV4().replace(/-/g, '');
    response.cookies.set(LOG_COOKIE_KEYS.log_session_id, sessionId, {
      expires: generateCookieExpire(30, 'minutes'),
      domain: generateCookieDomain(),
    });

    const pid = req.cookies.get(LOG_COOKIE_KEYS.log_pid)?.value ?? generateLogPid();
    if (pid) {
      response.cookies.set(LOG_COOKIE_KEYS.log_pid, pid, {
        expires: generateCookieExpire(100, 'years'),
        domain: generateCookieDomain(),
      });
    } else {
      console.error('log_pid is not set', req.headers.get('user-agent'));
    }

    try {
      const stringifiedLogData = JSON.stringify({
        user_id: getUserIdFromAccessToken({
          token: req.cookies.get('api-access-token')?.value ?? '',
          runtime: 'web',
        }),
        client_ip: getRequestIp(req),
        event_key: uuidV4(),
        lib_version: '1.0.0',
        utm: getAllUTM(req, response), // 모든 utm 쿠키
        pid,
        session_id: sessionId,
        session_timestamp: sessionTimestamp,
      });

      response.cookies.set(LOG_COOKIE_KEYS.log_data, stringifiedLogData, {
        expires: generateCookieExpire(30, 'minutes'),
        domain: generateCookieDomain(),
      });
    } catch (error) {
      // send error log
    }

    return next(req, event);
  };
};
